import { resolveCalculatorModelQueryWithFallbacks } from '@/app/lcv/components/sections/calculator/calculator-helpers';
import type {
  CategoryDefaultGrades,
  LcvModelWithFieldKey,
} from '@/services/lineup-model/lcv-product-compare/types';
import type { LcvModelCategory, LcvModelColor } from '@/types/tis-cms/types';

const DEFAULT_MAX_COLUMNS = 3;

export type CompareQueryResolverDeps = {
  allModels: Record<string, LcvModelWithFieldKey[]>;
  modelCategories: LcvModelCategory[];
  defaultModelCategory: LcvModelCategory;
  modelYear: string;
  defaultGradesByCategoryKey?: Record<string, CategoryDefaultGrades>;
  maxColumns?: number;
};

export type CompareColumnsFromQueryResult = {
  modelKeys: string[];
  colors: (LcvModelColor | undefined)[];
};

export const getCompareModelDedupeKey = (model: LcvModelWithFieldKey) =>
  model.fieldKey ?? '';

export const resolveCompareColumnsFromQueryParams = (
  rawParams: string[],
  deps: CompareQueryResolverDeps
): CompareColumnsFromQueryResult => {
  const maxColumns = deps.maxColumns ?? DEFAULT_MAX_COLUMNS;
  const modelKeys: string[] = [];
  const colors: (LcvModelColor | undefined)[] = [];
  const seenModelKeys = new Set<string>();

  for (const raw of rawParams) {
    if (modelKeys.length >= maxColumns) break;

    const param = raw.toLowerCase().trim();
    if (!param) continue;

    const outcome = resolveCalculatorModelQueryWithFallbacks(
      param,
      deps.allModels,
      deps.modelCategories,
      deps.defaultModelCategory,
      deps.modelYear,
      deps.defaultGradesByCategoryKey
    );
    if (!outcome) continue;

    const modelKey = getCompareModelDedupeKey(outcome.resolved.model);
    if (!modelKey || seenModelKeys.has(modelKey)) continue;

    seenModelKeys.add(modelKey);
    modelKeys.push(modelKey);
    colors.push(outcome.resolved.color);
  }

  while (modelKeys.length < maxColumns) {
    modelKeys.push('');
    colors.push(undefined);
  }

  return { modelKeys, colors };
};
